/**
 * RTWO Cryptography & Identity Engine — Provider Lifecycle Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { RTWOIdentityBackendDescriptor } from '../adapters/identity-backend-interface';
import type { RTWOKeyProviderDescriptor } from '../adapters/key-provider-interface';
import type { IProviderLifecycleService } from '../service-interfaces';
import type { IdentityBackendRegistry, KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class ProviderLifecycleService
  extends BaseCryptoService
  implements IProviderLifecycleService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.PROVIDER_LIFECYCLE;
  readonly serviceName = 'Provider Lifecycle Service';
  readonly description =
    'Manages connect/disconnect lifecycle for key providers and identity backends.';
  readonly layer = 'R6' as const;
  readonly integrations = ['R6 Security & Automation', 'Key Provider', 'Identity Backend'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly keyProviderRegistry: KeyProviderRegistry,
    private readonly identityBackendRegistry: IdentityBackendRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'ready';
  }

  async connectKeyProvider(): Promise<RTWOKeyProviderDescriptor['status']> {
    const provider = this.keyProviderRegistry.getActiveProvider();
    if (!this.keyProviderRegistry.isConfigured()) {
      return provider.getStatus();
    }

    await provider.connect();
    this.context.eventBus.emit(CryptoEventTypes.KEY_PROVIDER_CONNECTED, provider.providerId, {
      providerId: provider.providerId,
      status: provider.getStatus(),
    });
    return provider.getStatus();
  }

  async disconnectKeyProvider(): Promise<RTWOKeyProviderDescriptor['status']> {
    const provider = this.keyProviderRegistry.getActiveProvider();
    await provider.disconnect();
    return provider.getStatus();
  }

  async connectIdentityBackend(): Promise<RTWOIdentityBackendDescriptor['status']> {
    const backend = this.identityBackendRegistry.getActiveBackend();
    if (!this.identityBackendRegistry.isConfigured()) {
      return backend.getStatus();
    }

    await backend.connect();
    this.context.eventBus.emit(CryptoEventTypes.IDENTITY_BACKEND_CONNECTED, backend.backendId, {
      backendId: backend.backendId,
      status: backend.getStatus(),
    });
    return backend.getStatus();
  }

  async disconnectIdentityBackend(): Promise<RTWOIdentityBackendDescriptor['status']> {
    const backend = this.identityBackendRegistry.getActiveBackend();
    await backend.disconnect();
    return backend.getStatus();
  }

  getKeyProviderDescriptor(): RTWOKeyProviderDescriptor {
    return this.keyProviderRegistry.getActiveProvider().getDescriptor();
  }

  getIdentityBackendDescriptor(): RTWOIdentityBackendDescriptor {
    return this.identityBackendRegistry.getActiveBackend().getDescriptor();
  }

  isKeyProviderReady(): boolean {
    return this.keyProviderRegistry.getActiveProvider().isReady();
  }

  isIdentityBackendReady(): boolean {
    return this.identityBackendRegistry.getActiveBackend().isReady();
  }
}
